import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isMobileScreen } from '@apply/ui';
import { Button, ButtonTheme, Panel, PanelBorderRadius, PanelTheme, GenericAlertPopup, EmailBox, TextBox, PhoneBox } from '@apply/components';
import { Footer } from '../common_v2/components/Footer';
import { LogoLink } from '../common_v2/components/LogoLink';
import FileUploader from 'devextreme-react/file-uploader';
import { CheckBox } from 'devextreme-react/check-box';
import { v4 as uuidv4 } from 'uuid';
import { useFileUploadSettingsQuery, useUpdateBatchUserInfoMutation, UserInfo } from './queries/fileUploadQueries';
import './scss/fileuploadpage.scss';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface UploadedFile {
    id: number;
    name: string;
    size: number;
    status: 'uploading' | 'uploaded' | 'error';
    progress?: number;
    errorMessage?: string;
    batchId?: string;
}

const loadUserInfoFromStorage = (): UserInfo | null => {
    try {
        const stored = localStorage.getItem('fileUploadUserInfo');
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

const saveUserInfoToStorage = (userInfo: UserInfo) => {
    try {
        localStorage.setItem('fileUploadUserInfo', JSON.stringify(userInfo));
    } catch (error) {
        console.error('Failed to save user info:', error);
    }
};

export const FileUploadPage: React.FC = () => {
    const { data: settings, isLoading: isLoadingSettings } = useFileUploadSettingsQuery();
    const updateUserInfoMutation = useUpdateBatchUserInfoMutation();

    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const emailFromUrl = params.get('email') || '';

    const [batchId] = useState<string>(() => uuidv4());
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isCompleting, setIsCompleting] = useState<boolean>(false);
    const [email, setEmail] = useState<string>(emailFromUrl);
    const [emailIsValid, setEmailIsValid] = useState<boolean>(() => {
        // Validate initial email from URL
        if (!emailFromUrl || emailFromUrl.trim() === '') return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(emailFromUrl);
    });
    const fileUploaderRef = useRef<FileUploader>(null);
    const activeUploadsRef = useRef<Map<number, XMLHttpRequest>>(new Map());

    // User info screen state
    const [showUserInfoScreen, setShowUserInfoScreen] = useState<boolean>(true);
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [userEmail, setUserEmail] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [companyName, setCompanyName] = useState<string>('');
    const [firstNameValid, setFirstNameValid] = useState<boolean>(false);
    const [lastNameValid, setLastNameValid] = useState<boolean>(false);
    const [userEmailValid, setUserEmailValid] = useState<boolean>(false);
    const [phoneValid, setPhoneValid] = useState<boolean>(false);
    const [companyNameValid, setCompanyNameValid] = useState<boolean>(false);
    const [rememberMe, setRememberMe] = useState<boolean>(false);

    // Computed state: all fields valid
    // Check both validation flags AND actual values to handle cases where validation events haven't fired yet
    const allFieldsValid = useMemo(() => {
        return firstNameValid && firstName.trim().length > 0 &&
               lastNameValid && lastName.trim().length > 0 &&
               userEmailValid && userEmail.trim().length > 0 &&
               phoneValid && phone.trim().length > 0 &&
               companyNameValid && companyName.trim().length > 0;
    }, [firstNameValid, lastNameValid, userEmailValid, phoneValid, companyNameValid,
        firstName, lastName, userEmail, phone, companyName]);

    // Load user info from localStorage on mount
    useEffect(() => {
        const stored = loadUserInfoFromStorage();
        if (stored) {
            setFirstName(stored.firstName);
            setLastName(stored.lastName);
            setUserEmail(stored.email);
            setPhone(stored.phone);
            setCompanyName(stored.companyName);

            // Set all validation states to true since stored data was already validated
            setFirstNameValid(true);
            setLastNameValid(true);
            setUserEmailValid(true);
            setPhoneValid(true);
            setCompanyNameValid(true);

            // Check the "Remember me" checkbox since data was previously saved
            setRememberMe(true);
        }
    }, []);

    // Trigger validation for pre-populated email when upload screen becomes visible
    useEffect(() => {
        if (!showUserInfoScreen && emailFromUrl) {
            // Force validation by clearing and resetting the value
            setEmail('');
            setTimeout(() => {
                setEmail(emailFromUrl);
            }, 0);
        }
    }, [showUserInfoScreen, emailFromUrl]);

    // File validation settings from server
    const allowedExtensions = useMemo(() => {
        if (!settings?.allowedFileTypes) return [];
        return settings.allowedFileTypes.split(',').map(ext => ext.trim().toLowerCase());
    }, [settings?.allowedFileTypes]);

    const maxFileSizeMB = settings?.maxFileSizeMB ?? 250;
    const maxFileSizeBytes = Math.round(maxFileSizeMB * 1024 * 1024);
    const maxFilesPerBatch = settings?.maxFilesPerBatch ?? 10;
    const allowedFileTypes = settings?.allowedFileTypes ?? '';

    const validateFile = (file: File, pendingCount: number = 0): string | null => {
        // Check file count (including files being added in this batch)
        // Only count files that aren't in error state
        const validFileCount = files.filter(f => f.status !== 'error').length;
        if (validFileCount + pendingCount >= maxFilesPerBatch) {
            return `Maximum ${maxFilesPerBatch} files allowed per upload`;
        }

        // Check file size
        if (file.size > maxFileSizeBytes) {
            return `File size exceeds ${maxFileSizeMB}MB limit`;
        }

        // Check file type if restrictions exist
        if (allowedExtensions.length > 0) {
            const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
            if (!allowedExtensions.includes(fileExt)) {
                return `File type not allowed. Allowed types: ${allowedFileTypes}`;
            }
        }

        return null;
    };

    const handleContinue = async () => {
        const userInfo: UserInfo = {
            firstName,
            lastName,
            email: userEmail,
            phone,
            companyName
        };

        try {
            // Save user info to backend
            await updateUserInfoMutation.mutateAsync({
                batchId,
                userInfo
            });

            // Save to or remove from localStorage based on remember me checkbox
            if (rememberMe) {
                saveUserInfoToStorage(userInfo);
            } else {
                localStorage.removeItem('fileUploadUserInfo');
            }

            // Hide user info screen, show upload screen
            setShowUserInfoScreen(false);
        } catch (error) {
            console.error('Failed to save user info:', error);
            GenericAlertPopup({
                title: 'Error',
                children: error instanceof Error ? error.message : 'Failed to save user information. Please try again.',
                width: '350px',
            });
        }
    };

    const handleValueChanged = async (e: any) => {
        const selectedFiles = e.value as File[];

        if (!selectedFiles || selectedFiles.length === 0) return;

        // Check if adding these files would exceed the limit
        // Only count files that aren't in error state
        const validFileCount = files.filter(f => f.status !== 'error').length;
        const availableSlots = maxFilesPerBatch - validFileCount;
        const totalFilesSelected = selectedFiles.length;

        // If user selected more files than available slots, show alert
        if (totalFilesSelected > availableSlots) {
            const selectedText = `${totalFilesSelected} file${totalFilesSelected !== 1 ? 's' : ''}`;
            const remainingText = `only ${maxFilesPerBatch} total files are allowed per upload`;
            const uploadedText = availableSlots === 0
                ? 'No additional files'
                : `Only the first ${availableSlots} of these files`;
            GenericAlertPopup({
                title: 'File Limit',
                children: `You selected ${selectedText} but ${remainingText}. ${uploadedText} will be uploaded.`,
                width: '350px',
            });
        }

        // Only process files up to the limit
        const filesToProcess = selectedFiles.slice(0, availableSlots);
        const newFiles: UploadedFile[] = [];
        let validFilesAdded = 0; // Track how many valid files we've queued in this batch

        for (const file of filesToProcess) {
            // Validate file, passing count of valid files already added in this batch
            const validationError = validateFile(file, validFilesAdded);

            if (validationError) {
                // Add file with error status
                newFiles.push({
                    id: Date.now() + Math.random(),
                    name: file.name,
                    size: file.size,
                    status: 'error',
                    errorMessage: validationError,
                });
                continue;
            }

            // Add file with uploading status
            const tempId = Date.now() + Math.random();
            newFiles.push({
                id: tempId,
                name: file.name,
                size: file.size,
                status: 'uploading',
                progress: 0,
            });
            validFilesAdded++; // Increment count of valid files in this batch

            // Upload file
            uploadFile(file, tempId);
        }

        setFiles(prev => [...prev, ...newFiles]);

        // Reset the file uploader to ensure file validation will trigger on next file select
        if (fileUploaderRef.current) {
            fileUploaderRef.current.instance.reset();
        }
    };

    const uploadFile = async (file: File, tempId: number) => {
        setIsUploading(true);

        return new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('batchId', batchId);

            // Store XHR reference for cancellation
            activeUploadsRef.current.set(tempId, xhr);

            // Track upload progress
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percent = Math.round((event.loaded / event.total) * 100);
                    setFiles(prev =>
                        prev.map(f =>
                            f.id === tempId ? { ...f, progress: percent } : f
                        )
                    );
                }
            });

            // Handle successful upload
            xhr.addEventListener('load', () => {
                activeUploadsRef.current.delete(tempId);
                setIsUploading(false);

                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const result = JSON.parse(xhr.responseText);

                        // Update file status to uploaded
                        setFiles(prev =>
                            prev.map(f =>
                                f.id === tempId
                                    ? { ...f, status: 'uploaded' as const, progress: 100, batchId: result.batchId }
                                    : f
                            )
                        );
                        resolve();
                    } catch (error) {
                        console.error('Parse error:', error);
                        const errorMessage = 'Invalid server response';
                        setFiles(prev =>
                            prev.map(f =>
                                f.id === tempId
                                    ? { ...f, status: 'error' as const, errorMessage }
                                    : f
                            )
                        );
                        reject(new Error(errorMessage));
                    }
                } else {
                    // Handle HTTP error responses
                    let errorMessage = 'Upload failed';
                    try {
                        const errorData = JSON.parse(xhr.responseText);
                        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                            errorMessage = errorData.errors.join(', ');
                        }
                    } catch {
                        errorMessage = `Upload failed with status ${xhr.status}`;
                    }

                    setFiles(prev =>
                        prev.map(f =>
                            f.id === tempId
                                ? { ...f, status: 'error' as const, errorMessage }
                                : f
                        )
                    );
                    reject(new Error(errorMessage));
                }
            });

            // Handle network errors
            xhr.addEventListener('error', () => {
                activeUploadsRef.current.delete(tempId);
                setIsUploading(false);
                const errorMessage = 'Network error - please check your connection and try again';

                setFiles(prev =>
                    prev.map(f =>
                        f.id === tempId
                            ? { ...f, status: 'error' as const, errorMessage }
                            : f
                    )
                );
                reject(new Error(errorMessage));
            });

            // Handle request cancellation
            xhr.addEventListener('abort', () => {
                activeUploadsRef.current.delete(tempId);
                setIsUploading(false);
                const errorMessage = 'Upload cancelled';

                setFiles(prev =>
                    prev.map(f =>
                        f.id === tempId
                            ? { ...f, status: 'error' as const, errorMessage }
                            : f
                    )
                );
                reject(new Error(errorMessage));
            });

            // Send the request
            xhr.open('POST', `${API_BASE_URL}/fileupload/upload`);
            xhr.send(formData);
        });
    };

    const handleRemoveFile = (fileId: number) => {
        // Cancel active upload if it exists
        const xhr = activeUploadsRef.current.get(fileId);
        if (xhr) {
            xhr.abort();
        }

        // Remove file from state
        setFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const handleDoneUploading = async () => {
        setIsCompleting(true);

        try {
            const response = await fetch(`${API_BASE_URL}/fileupload/batch/${batchId}/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', },
                body: JSON.stringify({ email }),
            });

            if (!response.ok) {
                let errorMessage = 'Failed to complete batch';
                try {
                    const responseText = await response.text();
                    const errorData = JSON.parse(responseText);
                    if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                        errorMessage = errorData.errors.join(', ');
                    }
                } catch {
                    errorMessage = 'Failed to complete batch';
                }
                throw new Error(errorMessage);
            }

            // Show success message and reload page on dismiss
            GenericAlertPopup({
                title: 'Upload Complete',
                children: 'Your files have been uploaded successfully.',
                width: '350px',
                handleClose: () => {
                    window.location.reload();
                },
            });
        } catch (error) {
            console.error('Complete batch error:', error);
            GenericAlertPopup({
                title: 'Error',
                children: error instanceof Error ? error.message : 'Failed to complete upload. Please try again.',
                width: '350px',
            });
        } finally {
            setIsCompleting(false);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i)) + ' ' + sizes[i];
    };

    const hasUploadedFiles = files.some(f => f.status === 'uploaded');
    const showDoneButton = hasUploadedFiles && !isUploading && emailIsValid;

    const panelChildren = () => {
        if (isLoadingSettings) {
            return (
                <>
                    <div className={`preAuthenticationLogoLink ${isMobileScreen() ? 'mobile' : ''}`}>
                        <LogoLink />
                    </div>
                    <main>
                        <div className='fileUpload-page'>
                            <h2>File Uploader</h2>
                            <br />
                            <div className='loading'>
                                <span className='spinner'></span>
                                <span className='spinner-text'>Loading...</span>
                            </div>
                        </div>
                    </main>
                </>
            );
        }

        return (
            <>
                <div className={`preAuthenticationLogoLink ${isMobileScreen() ? 'mobile' : ''}`}>
                    <LogoLink />
                </div>
                <main>
                    {showUserInfoScreen ? (
                        <div className='user-info-screen'>
                            <div className='user-info-container'>
                                <h2>Contact Information</h2>
                                <TextBox
                                    id='first-name-input'
                                    label='First Name'
                                    value={firstName}
                                    onChange={(value) => setFirstName(value)}
                                    onValidationEvent={(isValid) => setFirstNameValid(isValid)}
                                    required={true}
                                    validateValue={(value) => value?.trim().length > 0}
                                    validateValueMessage='First name is required'
                                />
                                <TextBox
                                    id='last-name-input'
                                    label='Last Name'
                                    value={lastName}
                                    onChange={(value) => setLastName(value)}
                                    onValidationEvent={(isValid) => setLastNameValid(isValid)}
                                    required={true}
                                    validateValue={(value) => value?.trim().length > 0}
                                    validateValueMessage='Last name is required'
                                />
                                <EmailBox
                                    id='user-email-input'
                                    label='Email'
                                    value={userEmail}
                                    onChange={(value) => setUserEmail(value)}
                                    onValidationEvent={(isValid) => setUserEmailValid(isValid)}
                                    required={true}
                                />
                                <PhoneBox
                                    id='phone-input'
                                    label='Phone'
                                    value={phone}
                                    onChange={(value) => setPhone(value)}
                                    onValidationEvent={(isValid) => setPhoneValid(isValid)}
                                    required={true}
                                />
                                <TextBox
                                    id='company-name-input'
                                    label='Company Name'
                                    value={companyName}
                                    onChange={(value) => setCompanyName(value)}
                                    onValidationEvent={(isValid) => setCompanyNameValid(isValid)}
                                    required={true}
                                    validateValue={(value) => value?.trim().length > 0}
                                    validateValueMessage='Company name is required'
                                />
                                <div className='remember-me-container'>
                                    <CheckBox
                                        value={rememberMe}
                                        onValueChanged={(e) => setRememberMe(e.value)}
                                        text='Remember me'
                                    />
                                </div>
                                <Button
                                    id='continue-button'
                                    theme={ButtonTheme.Primary}
                                    width='100%'
                                    handleClick={handleContinue}
                                    disabled={!allFieldsValid || updateUserInfoMutation.isPending}
                                >
                                    {updateUserInfoMutation.isPending ? 'Saving...' : 'Continue'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className='fileUpload-page'>
                            <h2>File Uploader</h2>

                            <div className='email-input-container'>
                            <EmailBox
                                id='email-input'
                                label='Recipient Email Address'
                                value={email}
                                onChange={(e) => setEmail(e)}
                                onValidationEvent={(isValid) => setEmailIsValid(isValid)}
                                required={true}
                            />
                        </div>

                        <div className='upload-constraints'>
                            Maximum {maxFilesPerBatch} files, {maxFileSizeMB}MB each
                        </div>

                        <div className='upload-zone'>
                            <FileUploader
                                ref={fileUploaderRef}
                                multiple={true}
                                accept={allowedFileTypes || '*'}
                                uploadMode='useForm'
                                selectButtonText='SELECT A FILE'
                                labelText='or Drop a file here'
                                showFileList={false}
                                onValueChanged={handleValueChanged}
                            />
                        </div>

                        {files.length > 0 && (
                            <div className='file-list'>
                                {files.map(file => (
                                    <div key={file.id} className={`file-item ${file.status}`}>
                                        <div className='file-info'>
                                            {file.status !== 'uploaded' && (
                                                <button
                                                    className='remove-button'
                                                    onClick={() => handleRemoveFile(file.id)}
                                                    aria-label='Remove file'
                                                >
                                                    ×
                                                </button>
                                            )}
                                            <div className='file-details'>
                                                <span className='file-name'>{file.name}</span>
                                                <span className='file-size'>{formatFileSize(file.size)}</span>
                                            </div>
                                        </div>

                                        {file.status === 'uploading' && (
                                            <div className='progress-container'>
                                                <div className='progress-bar' style={{ width: `${file.progress || 0}%` }} />
                                                <span className='progress-text'>{file.progress || 0}%</span>
                                            </div>
                                        )}

                                        {file.status === 'uploaded' && (
                                            <div className='status-text uploaded'>Ready</div>
                                        )}

                                        {file.status === 'error' && (
                                            <div className='status-text error'>{file.errorMessage || 'Upload failed'}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {showDoneButton && (
                            <div className='done-button-container'>
                                <Button
                                    id='done-uploading-button'
                                    theme={ButtonTheme.Primary}
                                    width='300px'
                                    handleClick={handleDoneUploading}
                                    disabled={isCompleting}
                                >
                                    {isCompleting ? 'Completing...' : 'Done Uploading'}
                                </Button>
                            </div>
                        )}
                    </div>
                    )}
                    {!showUserInfoScreen && (
                        <button
                            type='button'
                            className='back-link'
                            onClick={() => setShowUserInfoScreen(true)}
                        >
                            ← Back to Contact Info
                        </button>
                    )}
                </main>
            </>
        );
    };

    return (
        <>
            <div className='app-container'>
                <div className='app-container authenticationContainer'>
                    {isMobileScreen() ? (
                        <div className='authenticationContainer mobile'>{panelChildren()}</div>
                    ) : (
                        <div className='authenticationContainer-desktop'>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Medium}
                                width={'90%'}
                            >
                                <div className='authenticationContainer-desktop-desktopWrapper'>{panelChildren()}</div>
                            </Panel>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};
